/* eslint-disable @typescript-eslint/default-param-last */

import { RootState } from '../';
import { getType } from 'typesafe-actions';
import { createApiActions, ActionsOf, createSubActions } from '../../helpers/actions';
import { PortfolioApi, PortfolioQuery, PortfolioResponse } from '../../api/PortfolioApi';
import { PortfolioSubscription } from '../../api/subscriptions/PortfolioSubscription';
import { apiSagaV2, subSaga } from '../../helpers/saga';
import { takeEvery } from 'redux-saga/effects';
import { OrderDirectionType } from '../../components/ui/DataTable';
import URI from 'urijs';

const DEFAULT_PAGE_COUNT = 25;

export const FilterView = {
  endUserNameAndAddress: 'endUserNameAndAddress',
  installerCompanyName: 'installerCompanyName',
  equipmentType: 'equipmentType',
  equipmentSerialNumber: 'equipmentSerialNumber',
  equipmentFirmwareVersion: 'equipmentFirmwareVersion',
  onlineStatus: 'onlineStatus',
  signalStrength: 'signalStrength',
} as const;

export type FilterViewType = (typeof FilterView)[keyof typeof FilterView];

export const productNamesToUserFriendlyNames = {
  eVaultClassic: 'eVault Classic',
  eVaultMaxRevA: 'eVault Max (Rev A)',
  eVaultMaxRevB: 'eVault Max (Rev B)',
  eFlexRevA: 'eFlex (Rev A)',
  eFlexRevB: 'eFlex (Rev B)',
  'Avalon HV BMS': 'Avalon BMS',
  eForce: 'eForce',
  eFlexMax: 'eFlex Max',
  'Avalon Inverter 7.6': 'Avalon 7.6kW',
  'Avalon Inverter 11.4': 'Avalon 11.4kW',
  Envy: 'Envy',
  Guardian: 'Guardian',
  'Smart Energy Panel': 'Smart Energy Panel',
};

export interface PortfolioFilter {
  endUserName?: string;
  endUserAddress?: string;
  installerCompanyName?: string;
  equipmentType?: string;
  equipmentQuantityOperator?: string;
  equipmentQuantity?: string;
  equipmentSerialNumber?: string[];
  serialNumberOperator?: string;
  equipmentFirmwareVersion?: string;
  equipmentFirmwareVersionOperator?: string;
  onlineStatus?: string;
  signalStrength?: string;
}

// TODO: change this to the actual types
export interface PortfolioFilterOptions {
  installers: { name: string; value: string }[];
  equipmentTypes: { name: string; value: string }[];
  equipmentFirmwareVersions: { Model: string; FirmwareVersion: string }[];
}

export interface PortfolioState extends PortfolioFilter {
  page: number;
  pageCount: number;
  sortBy: string;
  sortDir: OrderDirectionType;
  textMatch?: string;
}

export const FilterLabels: Partial<Record<FilterViewType, string>> = {
  [FilterView.endUserNameAndAddress]: 'End user name and address',
  [FilterView.equipmentType]: 'Equipment type',
  [FilterView.equipmentFirmwareVersion]: 'Firmware version',
  [FilterView.installerCompanyName]: 'Installer',
  [FilterView.onlineStatus]: 'Online/Offline status',
  [FilterView.equipmentSerialNumber]: 'Serial number',
  // Post Launch
  // [FilterView.signalStrength]: 'Signal strength',
} as const;

export const ChipLabels: Partial<Record<keyof PortfolioFilter, string>> = {
  endUserName: 'End user name',
  endUserAddress: 'End user address',
  installerCompanyName: 'Installer',
  equipmentType: 'Equipment type',
  equipmentSerialNumber: 'Serial number',
  equipmentFirmwareVersion: 'Firmware version',
  onlineStatus: 'Online/Offline status',
  signalStrength: 'Signal strength',
} as const;

export const FilterGroups: Partial<Record<FilterViewType, string[]>> = {
  [FilterView.equipmentSerialNumber]: ['serialNumber'],
  [FilterView.equipmentFirmwareVersion]: [
    'equipmentFirmwareVersion',
    'equipmentFirmwareVersionOperator',
    'equipmentType',
  ],
  [FilterView.equipmentType]: ['equipmentType', 'equipmentQuantityOperator', 'equipmentQuantity'],
};

export const emptyState: PortfolioState = {
  page: 1,
  sortBy: 'alarmStatus',
  sortDir: 'asc',
  pageCount: DEFAULT_PAGE_COUNT,
  endUserName: undefined,
  endUserAddress: undefined,
  installerCompanyName: undefined,
  equipmentType: undefined,
  equipmentQuantityOperator: undefined,
  equipmentQuantity: undefined,
  equipmentSerialNumber: undefined,
  equipmentFirmwareVersion: undefined,
  equipmentFirmwareVersionOperator: undefined,
  onlineStatus: undefined,
  signalStrength: undefined,
};

export interface PortfolioPersistantState {
  lastUrl?: string;
  columns: string[];
}

export const stateToQuery = (state: PortfolioState): PortfolioQuery => ({
  endUserName: state.endUserName,
  endUserAddress: state.endUserAddress,
  installerCompanyName: state.installerCompanyName,
  equipmentType: state.equipmentType,
  equipmentQuantityOperator: state.equipmentQuantityOperator,
  equipmentQuantity: state.equipmentQuantity,
  equipmentSerialNumber: state.equipmentSerialNumber,
  equipmentFirmwareVersion: state.equipmentFirmwareVersion,
  equipmentFirmwareVersionOperator: state.equipmentFirmwareVersionOperator,
  onlineStatus: state.onlineStatus,
  signalStrength: state.signalStrength,
  sortBy: state.sortBy || 'alarmStatus',
  sortAscending: state.sortDir === 'asc',
  pageNumber: state.page,
  pageSize: state.pageCount || DEFAULT_PAGE_COUNT,
  nameAddressFilter: state.textMatch,
});

// TODO: review this
export const urlParser = (url: string) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rawState = new URI(url).search(true) as any;

  // ensure there is a property for each filter
  ['customers', 'statuses', 'utilities', 'zipCodes', 'gridServicesEnrollmentStatuses'].forEach(
    (key) => {
      if (rawState[key] && !(rawState[key] instanceof Array)) {
        rawState[key] = [rawState[key]];
      } else if (!rawState[key]) {
        rawState[key] = [];
      }
    }
  );

  // parse ids as ints
  ['customers', 'utilities'].forEach((key) => {
    rawState[key] = rawState[key].map((id: string) => parseInt(id, 10));
  });

  rawState.page = rawState.page ? parseInt(rawState.page, 10) : 1;
  return rawState as PortfolioState;
};

/* ----- actions ----- */

export const queryPortfolio = createApiActions<PortfolioResponse>()(
  'portfolioPage/queryPortfolio',
  'portfolioPage/queryPortfolioSuccess',
  'portfolioPage/queryPortfolioError',
  (query: PortfolioQuery) => query
);

export const subPortfolio = createSubActions<PortfolioResponse>()(
  'portfolioPage/subStart',
  'portfolioPage/subUpdate',
  'portfolioPage/subError',
  'portfolioPage/subStop',
  (query: PortfolioQuery) => query
);

export const fetchPortfolioOptions = createApiActions<PortfolioFilterOptions>()(
  'portfolioPage/fetchOptions',
  'portfolioPage/fetchOptionsSuccess',
  'portfolioPage/fetchOptionsError',
  () => true
);

export const downloadCSV = createApiActions<{ success: boolean; error?: unknown }>()(
  'portfolioPage/downloadCSV',
  'portfolioPage/downloadCSVSuccess',
  'portfolioPage/downloadCSVError',
  (query: PortfolioQuery) => query
);

/* ----- sagas ----- */

const queryPortfolioSaga = apiSagaV2(queryPortfolio, (connection, action) => {
  const query = action.payload;
  return new PortfolioApi(connection).querySiteSummaries(query);
});

const subPortfolioSaga = subSaga(subPortfolio, (connection, action) => {
  const subscription = new PortfolioSubscription(connection);
  subscription.start(action.payload);
  return subscription;
});

// TODO: review this
const fetchPortfolioOptionsSaga = apiSagaV2(fetchPortfolioOptions, async (connection) => {
  const apiOptions = await new PortfolioApi(connection).fetchPortfolioOptions();
  // we need to map to the frontend naming convention here

  return {
    installers: apiOptions.installers.map((i) => ({ name: i, value: i })),
    equipmentTypes: apiOptions.equipmentTypes.map((e) => ({
      name: productNamesToUserFriendlyNames[e as keyof typeof productNamesToUserFriendlyNames] || e,
      value: e,
    })),
    equipmentFirmwareVersions: apiOptions.equipmentFirmwareVersions,
  } as PortfolioFilterOptions;
});

const downloadCSVSaga = apiSagaV2(
  downloadCSV,
  async (connection, action): Promise<{ success: boolean; error?: unknown }> => {
    try {
      const query = action.payload;
      const fileBlob = await new PortfolioApi(connection).downloadCSV(query);

      const downloadUrl = window.URL.createObjectURL(fileBlob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', 'portfolio.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }
);

export function* portfolioPageSagas() {
  yield takeEvery(getType(queryPortfolio.request), queryPortfolioSaga);
  yield takeEvery(getType(fetchPortfolioOptions.request), fetchPortfolioOptionsSaga);
  yield takeEvery(getType(subPortfolio.start), subPortfolioSaga);
  yield takeEvery(getType(downloadCSV.request), downloadCSVSaga);
}

/* ----- selectors ----- */

export const getSiteSummaries = (rootState: RootState) => {
  return rootState.contexts.portfolioPage.results?.sites;
};

export const getTotalResults = (rootState: RootState) => {
  return rootState.contexts.portfolioPage.results?.totalMatchingSites;
};

export const getFilterOptions = (rootState: RootState) => {
  return rootState.contexts.portfolioPage.options;
};

/* ----- reducer ----- */

// Note: query state is persisted in the url not this store
export interface PortfolioPageState {
  results?: PortfolioResponse;
  options?: PortfolioFilterOptions;
  loadingDownload?: boolean;
  downloadSuccess?: boolean;
  downloadError?: unknown;
}

const initialState = {
  results: undefined,
  options: undefined,
};

type PortfolioPageActions =
  | ActionsOf<typeof queryPortfolio | typeof fetchPortfolioOptions>
  | ActionsOf<typeof subPortfolio>
  | ActionsOf<typeof downloadCSV>;

export const portfolioPageReducer = (
  state: PortfolioPageState = initialState,
  action: PortfolioPageActions
): PortfolioPageState => {
  switch (action.type) {
    case getType(subPortfolio.update):
    case getType(queryPortfolio.success):
      return {
        ...state,
        results: action.payload.result,
      };
    case getType(fetchPortfolioOptions.success):
      return {
        ...state,
        options: action.payload.result,
      };
    case getType(downloadCSV.request):
      return {
        ...state,
        loadingDownload: true,
        downloadError: null,
      };
    case getType(downloadCSV.success):
      return {
        ...state,
        loadingDownload: false,
        downloadSuccess: action.payload.result.success,
        downloadError: action.payload.result.error,
      };
    default:
      return state;
  }
};
